/**
 * j file upload <file>
 * Upload HTML/Markdown/image file and get public preview URL
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { promises as fs } from 'fs';
import { resolve, relative, basename } from 'path';
import { ConfigService } from '../../services/config.service.js';
import { SettingsService } from '../../services/settings.service.js';
import { FileService } from '../../services/file.service.js';
import { prepareUploadBuffer } from '../../utils/file-upload-prepare.js';
import { getContentTypeLabel, formatUploadSize } from '../../utils/file-upload-rules.js';

export function createFileUploadCommand(): Command {
    return new Command('upload')
        .description('Upload file (HTML/Markdown/Image), get public preview URL')
        .argument('<file>', 'Path to HTML, Markdown, PNG, JPG, WebP, or GIF file')
        .option('--slug <slug>', 'Custom slug')
        .option('--project-id <id>', 'Project ID (default: auto-detect)')
        .option('--personal', 'Force personal scope')
        .option('--open', 'Open URL in browser after upload')
        .option('-j, --json', 'JSON output')
        .action(async (file: string, opts) => {
            try {
                const configService = new ConfigService();
                const config = await configService.load();
                if (!config) {
                    console.error(chalk.red('Chưa đăng nhập. Chạy "j auth" trước.'));
                    process.exit(1);
                }

                // Read file
                const filePath = resolve(file);
                const filename = basename(filePath);

                const content = await fs.readFile(filePath);
                const prepared = await prepareUploadBuffer(content, filename);
                const uploadFilename = prepared.filename;
                const base64 = prepared.buffer.toString('base64');

                // Determine scope
                let scope: 'personal' | 'project' = 'personal';
                let projectId: string | undefined;
                let relPath: string | undefined;

                if (opts.personal) {
                    scope = 'personal';
                } else if (opts.projectId) {
                    scope = 'project';
                    projectId = opts.projectId;
                    relPath = relative(process.cwd(), filePath);
                    if (relPath.startsWith('..')) relPath = undefined;
                } else {
                    // Auto-detect from settings
                    const settings = new SettingsService();
                    const detectedProjectId = settings.getProjectId();
                    if (detectedProjectId) {
                        scope = 'project';
                        projectId = detectedProjectId;
                        relPath = relative(process.cwd(), filePath);
                        if (relPath.startsWith('..')) relPath = undefined;
                    }
                }

                if (!opts.json && scope === 'personal' && !opts.personal) {
                    console.log(chalk.dim('Không detect được project → upload to personal files'));
                }

                const service = new FileService();
                let result;
                try {
                    result = await service.upload(config, {
                        content: base64,
                        filename: uploadFilename,
                        filePath: relPath,
                        slug: opts.slug,
                        scope,
                        projectId,
                    });
                } catch (uploadError) {
                    // Auto-detect project failed (not a member) → fallback to personal
                    const isAutoDetect = scope === 'project' && !opts.projectId && !opts.personal;
                    const isAccessDenied = uploadError instanceof Error && uploadError.message.includes('not a project member');
                    if (isAutoDetect && isAccessDenied) {
                        if (!opts.json) {
                            console.log(chalk.dim(`Project "${projectId}" chưa đăng ký → upload to personal files`));
                        }
                        result = await service.upload(config, {
                            content: base64,
                            filename: uploadFilename,
                            slug: opts.slug,
                            scope: 'personal',
                        });
                    } else {
                        throw uploadError;
                    }
                }

                if (opts.json) {
                    console.log(JSON.stringify(result, null, 2));
                } else {
                    console.log();
                    const typeLabel = getContentTypeLabel(result.contentType);
                    if (result.isOverride) {
                        console.log(chalk.yellow('⟳ Override:') + ` ${result.filename} ${chalk.dim(`(${typeLabel})`)}`);
                    } else {
                        console.log(chalk.green('✓ Uploaded:') + ` ${result.filename} ${chalk.dim(`(${typeLabel})`)}`);
                    }
                    if (prepared.compressed) {
                        console.log(chalk.dim(`  Compressed: ${formatUploadSize(prepared.originalSize)} → ${formatUploadSize(prepared.storedSize)}`));
                    }
                    console.log(chalk.cyan('  URL:') + ` ${result.url}`);
                    console.log(chalk.dim(`  Scope: ${result.scope}${result.projectId ? ` | Project: ${result.projectId}` : ''}`));
                    console.log(chalk.dim(`  Size: ${(result.contentSize / 1024).toFixed(1)}KB | ID: ${result.id}`));
                    console.log();
                }

                // Auto-open
                if (opts.open) {
                    const { exec } = await import('child_process');
                    const openCmd = process.platform === 'darwin' ? 'open' : process.platform === 'win32' ? 'start' : 'xdg-open';
                    exec(`${openCmd} "${result.url}"`);
                }

            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                console.error(chalk.red(`Lỗi: ${message}`));
                if (message.includes('quota') || message.includes('dung lượng')) {
                    console.error(chalk.dim('Gợi ý: chạy "j file quota", "j file list", rồi "j file delete <id>" để xoá file cũ.'));
                }
                process.exit(1);
            }
        });
}
